/**
 * Users feature DTOs — the wire shapes of the IDN customer-Administrator
 * user-management surface (`src/Ami.Identity/Users/Endpoints/UserContracts.cs`),
 * camelCase on the wire (ASP.NET `JsonSerializerDefaults.Web`).
 *
 * Relocated here from `src/fixtures/users.ts` (foundation breadcrumb): the
 * feature owns its types; the fixture file now imports these and stays
 * fixtures-only.
 */

/** Lighter row returned by `GET /api/v1/users` (`UserSummaryResponse`). */
export interface UserSummaryResponse {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
  /** Computed primary role: one of {@link RoleFilter}. */
  primaryRole: string;
  /** HR attributes shown as roster columns (nullable). */
  employeeNumber: string | null;
  title: string | null;
  position: string | null;
  /** Manager's display name, or null when the user has no manager. */
  managerFullName: string | null;
  /**
   * HR-feed job code. **Export-only** (AU-65) — the roster grid renders `position`,
   * not this — and carried on the summary rather than a separate export shape so the
   * file and the screen read one model.
   */
  jobCode: string | null;
  /**
   * The manager's employee number, beside the display name the grid shows. Export-only
   * (AU-65, AU-75): a name is ambiguous in a roster where people share one, so a
   * spreadsheet reconciling reporting lines joins this to the Employee number column.
   */
  managerEmployeeNumber: string | null;
}

/** Paginated envelope returned by `GET /api/v1/users` (`UserListResponse`). */
export interface UserListResponse {
  items: UserSummaryResponse[];
  total: number;
  take: number;
  skip: number;
}

/**
 * Full profile returned by `GET /api/v1/users/{id}` (`UserDetailResponse`).
 * Note: the API returns `firstName`/`lastName` (no server-side `fullName`) and
 * `managerId`/`managerFullName` (no nested `manager`) — the screen composes the
 * display name from the parts. `roles` is a flat list of role-name strings and
 * already includes the computed `Manager` when `directReportCount > 0`.
 */
export interface UserDetailResponse {
  id: string;
  customerId: string;
  email: string;
  firstName: string;
  lastName: string;
  employeeNumber: string | null;
  title: string | null;
  jobCode: string | null;
  position: string | null;
  managerId: string | null;
  managerFullName: string | null;
  directReportCount: number;
  contactPhoneNumber: string | null;
  isActive: boolean;
  isBuyUpEnabled: boolean;
  /**
   * True when the customer's HR feed owns this user's identity fields (set by
   * ingestion on create/update-match). The edit form renders the HR-owned fields
   * (name, employee #, title, job code, position, manager) read-only when true —
   * edits would be overwritten on the next HR import (TD-201).
   */
  isHrManaged: boolean;
  receivesUsageAlerts: boolean;
  receivesBillingReports: boolean;
  preferredLanguage: string;
  preferredTimeZone: string | null;
  roles: string[];
  createdAt: string;
  updatedAt: string;
  lastSignInAt: string | null;
}

/** Body for `POST /api/v1/users` (`CreateUserRequest`). Email is editable only here. */
export interface CreateUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  employeeNumber?: string | null;
  title?: string | null;
  jobCode?: string | null;
  position?: string | null;
  managerId?: string | null;
  contactPhoneNumber?: string | null;
}

/** Body for `PUT /api/v1/users/{id}` (`UpdateUserRequest`). Email is not editable here. */
export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  employeeNumber?: string | null;
  title?: string | null;
  jobCode?: string | null;
  position?: string | null;
  managerId?: string | null;
  contactPhoneNumber?: string | null;
  isBuyUpEnabled: boolean;
}

/**
 * The only two roles an Administrator can grant/revoke through the customer
 * surface. `SiteAdministrator` is never offered here (block 021 / SiteAdmin
 * surface), and `Manager` (computed from direct reports) / `User` (implicit)
 * have no persisted assignment — this hard-coded tuple is the FE half of the
 * privilege-escalation guard (PRD IDN §3.5, §3.8): the manage-roles form cannot
 * express any grant beyond what an Administrator is permitted to make.
 */
export const GRANTABLE_ROLES = ['Administrator', 'RequestDelegate'] as const;
export type GrantableRole = (typeof GRANTABLE_ROLES)[number];

/** Roles that are surfaced but NOT grantable here (informational only). */
export const AUTOMATIC_ROLES = ['Manager', 'User'] as const;
export type AutomaticRole = (typeof AUTOMATIC_ROLES)[number];

/** Roster role-filter vocabulary (`SiteAdministrator` is intentionally excluded). */
export const ROLE_FILTER_OPTIONS = ['Administrator', 'RequestDelegate', 'Manager', 'User'] as const;
export type RoleFilter = (typeof ROLE_FILTER_OPTIONS)[number];

/** Status filter tri-state; mirrors the API's `active` query param. */
export type ActiveFilter = 'true' | 'false' | 'all';

/** Query parameters accepted by the roster read model. */
export interface UsersListParams {
  role?: RoleFilter;
  active?: ActiveFilter;
  search?: string;
  isBuyUpEnabled?: boolean;
  take?: number;
  skip?: number;
}

/** Minimum search length the server accepts (`UserSearchSpecification.MinTermLength`). */
export const MIN_SEARCH_LENGTH = 2;

/** Default roster page size (`UserEndpoints.DefaultTake`). */
export const DEFAULT_TAKE = 50;

/**
 * Page size for user *pickers*.
 *
 * A picker shows one page and searches the rest server-side, so the page is the
 * convenience list — not the candidate set (BUG-034). It was set to the server's
 * 200-row ceiling, which meant every picker pulled 200 rows on open and 200 more
 * per settled term to render a dropdown showing eight at a time. A page a little
 * longer than the dropdown is all that is useful; anything past it is what the
 * search is for (BUG-042).
 */
export const USER_PICKER_TAKE = 25;

/** A single edge of a manager-cycle `cyclePath` extension on a 400 problem. */
export interface CyclePathNode {
  userId: string;
  email: string;
}

/** Which cascade level resolved a user's effective budget. */
export type EntitlementSource = 'UserOverride' | 'AssignmentRule';

/**
 * A user's resolved (effective) budget — `GET /api/v1/users/{id}/entitlements`
 * (`EffectiveBudgetResponse`). Limit fields are null when the profile leaves
 * that dimension uncapped ("No limit").
 */
export interface EffectiveBudget {
  profileId: string;
  profileName: string;
  dataLimitGB: number | null;
  costLimit: number | null;
  maxLines: number | null;
  source: EntitlementSource;
}

/** A user's resolved equipment package. Packages have no user-level override. */
export interface EffectivePackage {
  packageId: string;
  name: string;
  deviceCount: number;
  planCount: number;
  source: 'AssignmentRule';
}

/**
 * Resolved entitlements for the user-detail hero (`GET /users/{id}/entitlements`,
 * `UserEntitlementsResponse`). `budget` is null and `packages` is empty when
 * nothing is configured. `packages` carries more than one entry when the user's
 * assignment rules stack (DDR-CAT-003), in evaluation order.
 */
export interface UserEntitlementsResponse {
  budget: EffectiveBudget | null;
  packages: EffectivePackage[];
}

/**
 * One selectable request subject from `GET /api/v1/me/submit-candidates`
 * (`SubmitCandidateResponse`). Narrower than {@link UserSummaryResponse} on
 * purpose — the picker needs a name, an identifier and two disambiguating
 * attributes, and this read is reachable by every role, so it exposes nothing
 * the Administrator roster read is gated for.
 */
export interface SubmitCandidateResponse {
  id: string;
  email: string;
  fullName: string;
  employeeNumber: string | null;
  title: string | null;
}

/**
 * Envelope returned by `GET /api/v1/me/submit-candidates` — one searched, capped
 * page of selectable subjects (BUG-042; it used to be the whole roster).
 *
 * `hasMore` says the cap hid matches, so the picker can ask for a narrower term
 * rather than presenting the alphabetical first page as the whole answer.
 * `self` is the caller's own candidate row, returned separately so the form can
 * always label its default "for you" pick — a searched page gives no guarantee
 * the caller is in it.
 */
export interface SubmitCandidatesResponse {
  items: SubmitCandidateResponse[];
  hasMore: boolean;
  self: SubmitCandidateResponse | null;
}
